import React, { useState } from "react";

interface YouTubeLiteProps {
  // The YouTube video id, e.g. "S9gZeTMSY9c" from
  // https://www.youtube.com/watch?v=S9gZeTMSY9c
  videoId: string;
  // Accessible label / video title used for the iframe + play button.
  title: string;
}

// A lightweight YouTube embed. It renders only a static thumbnail (served
// from YouTube's own CDN, so nothing ships in our bundle) plus a play button.
// The heavy YouTube iframe player is mounted on demand — the first time the
// user clicks play — which keeps the page's initial load fast and avoids
// pulling YouTube's ~1MB+ of player scripts before anyone asks for the video.
const YouTubeLite: React.FC<YouTubeLiteProps> = ({ videoId, title }) => {
  const [activated, setActivated] = useState(false);
  // maxresdefault isn't generated for every upload (older / low-res videos),
  // so on a load error we fall back to hqdefault, which always exists.
  const [thumbnail, setThumbnail] = useState(
    `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`
  );

  if (activated) {
    return (
      // The poster stays behind the iframe so there's no black flash while
      // YouTube's player scripts load.
      <div
        className="portfolio-carousel-youtube-frame"
        style={{ backgroundImage: `url(${thumbnail})` }}
      >
        <iframe
          className="portfolio-carousel-video"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="portfolio-carousel-youtube-poster"
      style={{ backgroundImage: `url(${thumbnail})` }}
      onClick={() => setActivated(true)}
      aria-label={`Play ${title}`}
    >
      {/* Hidden probe image: detects a missing maxres thumbnail and swaps in
          the guaranteed hqdefault fallback. */}
      <img
        src={thumbnail}
        alt=""
        aria-hidden="true"
        style={{ display: "none" }}
        onError={() =>
          setThumbnail(`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`)
        }
      />
      <span className="portfolio-carousel-youtube-play" aria-hidden="true" />
    </button>
  );
};

export default YouTubeLite;
